// Main pipeline
export { executeCoastingPipeline } from './pipeline'

// Types
export {
  type CoastingPeriod,
  type CoastingConfig,
  DEFAULT_COASTING_CONFIG,
} from './types'

// Physics
export { calculateCoastingIntensity, calculateCombinedGForce } from './physics'

// Data collection
export { collectAndClassifyCoastingData } from './dataCollection'

// DSP functions
export {
  removeAnomalies,
  fillCoastingGaps,
  removeIsolatedSpikes,
} from './anomalyRemoval'

export {
  applyFinalIntensitySmoothing,
  applyPostSmoothingCleanup,
} from './visualSmoothing'

export {
  isRapidTransition,
  analyzeTransition,
  applyTransitionInfluence,
  TRANSITION_TIMING,
  TRANSITION_INFLUENCE,
  type TransitionTiming,
  type TransitionSpeed,
  type TransitionAnalysis,
} from './transitionDetection'

// Vehicle strategies
export {
  type VehicleStrategy,
  type VehiclePhysicsConfig,
} from './vehicles/VehicleStrategy'

export { MotorcycleStrategy } from './vehicles/MotorcycleStrategy'
export { CarStrategy } from './vehicles/CarStrategy'
export { GoKartStrategy } from './vehicles/GoKartStrategy'

export {
  createVehicleStrategy,
  getSupportedVehicleTypes,
  isValidVehicleType,
  DEFAULT_VEHICLE_TYPE,
  type VehicleType,
} from './vehicles/createVehicleStrategy'

// Constants
export * from './constants'

// Re-export for convenience: helper to build coasting map from periods
export function buildCoastingMap(
  coastingPeriods: readonly { distance: number; coastingIntensity: number }[]
): Map<number, number> {
  const coastingMap = new Map<number, number>()
  for (const period of coastingPeriods) {
    // Round distance to nearest 5m bucket (matching DISTANCE_BUCKET_SIZE_METERS)
    const bucketDistance = Math.round(period.distance / 5) * 5
    coastingMap.set(bucketDistance, period.coastingIntensity * 100)
  }
  return coastingMap
}

/**
 * Get coasting value at a specific distance with linear interpolation.
 *
 * The coasting data is stored at 5m intervals. When looking up a value
 * at an arbitrary distance (e.g., 167m), we interpolate between the
 * surrounding buckets (165m and 170m) for smooth transitions.
 */
export function getCoastingAtDistance(
  coastingMap: Map<number, number>,
  distanceMeters: number
): number {
  const BUCKET_SIZE = 5

  // Find the lower and upper bucket boundaries
  const lowerBucket = Math.floor(distanceMeters / BUCKET_SIZE) * BUCKET_SIZE
  const upperBucket = lowerBucket + BUCKET_SIZE

  const lowerValue = coastingMap.get(lowerBucket)
  const upperValue = coastingMap.get(upperBucket)

  // If we have both values, interpolate
  if (lowerValue !== undefined && upperValue !== undefined) {
    const t = (distanceMeters - lowerBucket) / BUCKET_SIZE
    return lowerValue + (upperValue - lowerValue) * t
  }

  // If we only have one, use it (extends the value to nearby points)
  if (lowerValue !== undefined) {
    return lowerValue
  }
  if (upperValue !== undefined) {
    return upperValue
  }

  // No data at all - return 0
  return 0
}
